import { CalendarDays, MapPin, CheckCircle2 } from "lucide-react";
import Reveal from "./Reveal";

// Templates d'interventions par métier — variés pour donner un signal de fraîcheur SEO
const TEMPLATES = {
  plomberie: [
    "Recherche et réparation de fuite d'eau",
    "Débouchage de canalisation cuisine",
    "Remplacement de chauffe-eau électrique 200L",
    "Réparation d'une fuite sous évier",
    "Débouchage WC bouché à la ventouse",
    "Changement de mitigeur salle de bain",
    "Intervention dégât des eaux copropriété",
    "Remplacement d'un groupe de sécurité",
    "Détection de fuite encastrée mur",
    "Pose d'un WC suspendu",
    "Réparation d'une chasse d'eau qui fuit",
    "Remplacement d'un ballon d'eau chaude",
  ],
  serrurerie: [
    "Ouverture de porte claquée sans dégât",
    "Remplacement d'un cylindre européen",
    "Changement de serrure multipoints",
    "Extraction d'une clé cassée dans le barillet",
    "Blindage d'une porte d'appartement",
    "Sécurisation après tentative d'effraction",
    "Pose d'une serrure certifiée A2P**",
    "Ouverture de coffre-fort bloqué",
    "Dépannage suite à perte de clés",
    "Installation d'un cylindre haute sécurité Vachette",
    "Remplacement d'une gâche électrique",
    "Réglage d'une porte blindée",
  ],
  electricite: [
    "Réparation d'un tableau électrique disjoncté",
    "Remplacement d'un disjoncteur différentiel 30 mA",
    "Diagnostic panne de courant partielle",
    "Court-circuit sur circuit prises salon",
    "Mise aux normes NF C 15-100 tableau",
    "Remplacement d'une prise électrique brûlée",
    "Installation d'un interphone Bticino",
    "Dépannage VMC hygroréglable",
    "Pose de spots LED encastrés",
    "Remplacement d'un interrupteur qui chauffe",
    "Réparation d'une ligne à la terre défaillante",
    "Installation d'un tableau électrique neuf 3 rangées",
  ],
};

const STATUS_LABELS = ["Résolue", "Terminée", "Réglée", "Effectuée"];

// Hash simple pour rendre les intervention stables mais variées par (dept, metier)
function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function formatDate(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function RecentInterventions({ metierKey, dept }) {
  const templates = TEMPLATES[metierKey] || [];
  const seed = hash(`${dept.code}-${metierKey}`);
  const items = [];
  const usedTpl = new Set();
  const usedCity = new Set();
  for (let i = 0; i < 6; i++) {
    // Sélection déterministe mais mixée
    const t = templates[(seed + i * 7) % templates.length];
    const c = dept.cities[(seed + i * 5) % dept.cities.length];
    // Force variation si doublon
    let tpl = t;
    let city = c;
    let tries = 0;
    while ((usedTpl.has(tpl) || usedCity.has(city)) && tries < 8) {
      const idx = (seed + (i + tries) * 3) % templates.length;
      const cidx = (seed + (i + tries) * 4) % dept.cities.length;
      tpl = templates[idx];
      city = dept.cities[cidx];
      tries++;
    }
    usedTpl.add(tpl);
    usedCity.add(city);
    const daysAgo = ((seed + i * 3) % 27) + 1;
    const status = STATUS_LABELS[(seed + i) % STATUS_LABELS.length];
    items.push({ tpl, city, daysAgo, status });
  }

  return (
    <section data-testid="recent-interventions" className="py-16 md:py-20 bg-slate-50 border-t border-slate-200">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="overline">Actualités & interventions</span>
            <h2 className="mt-3 font-display font-extrabold text-ink text-3xl md:text-4xl">
              Nos dernières interventions {dept.prep}
            </h2>
            <p className="mt-4 text-ink-soft leading-relaxed">
              Un aperçu des dépannages réalisés récemment par nos artisans dans les communes de {dept.name}.
              Interventions traçables, avec compte rendu détaillé et facture nominative.
            </p>
          </div>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <article className="card-soft p-6 h-full hover:translate-y-0">
                <div className="flex items-center gap-2 text-xs text-ink-soft">
                  <CalendarDays className="h-3.5 w-3.5" />
                  <time>{formatDate(it.daysAgo)}</time>
                </div>
                <h3 className="mt-3 font-display font-bold text-ink text-lg leading-tight">
                  {it.tpl}
                </h3>
                <div className="mt-3 flex items-center gap-1.5 text-sm text-ink-soft">
                  <MapPin className="h-3.5 w-3.5 text-brand" />
                  <span className="font-medium">{it.city}</span>
                  <span className="text-ink-soft/60">·</span>
                  <span>{dept.label}</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-green">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {it.status}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <p className="mt-10 text-center text-ink-soft text-sm">
          Interventions réalisées ces 30 derniers jours ({dept.name} — {dept.code}).
          Historique disponible sur demande.
        </p>
      </div>
    </section>
  );
}
